import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles,
  Wand2,
  Image as ImageIcon,
  Crown,
  CheckCircle2,
  Download,
  Share2,
  RefreshCw,
  AlertCircle,
  UploadCloud,
  ChevronRight,
  ChevronLeft,
  Calendar,
  MapPin,
  Clock,
  User,
  Heart,
  PartyPopper,
  ShieldAlert,
  Copy,
  Check,
  X,
  Gift,
  FileImage,
  Eye,
  ExternalLink,
} from 'lucide-react';
import {
  fetchActiveAiStyles,
  generateAiArtifact,
  fetchMyAiGenerations,
  AiStyle,
  AiGenerationRecord,
  type CanonicalAspectRatio,
} from './aiInvitationsService';

export const AiInvitationsPage: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // View Mode & Gallery State
  const [viewMode, setViewMode] = useState<'wizard' | 'gallery'>('wizard');
  const [myGenerations, setMyGenerations] = useState<AiGenerationRecord[]>([]);
  const [loadingMyGenerations, setLoadingMyGenerations] = useState<boolean>(false);
  const [galleryFilter, setGalleryFilter] = useState<'all' | 'wedding' | 'birthday'>('all');
  const [selectedGalleryItem, setSelectedGalleryItem] = useState<AiGenerationRecord | null>(null);

  const [step, setStep] = useState<number>(1);
  const [occasion, setOccasion] = useState<'wedding' | 'birthday'>('wedding');
  const [styles, setStyles] = useState<AiStyle[]>([]);
  const [loadingStyles, setLoadingStyles] = useState<boolean>(false);
  const [selectedStyle, setSelectedStyle] = useState<AiStyle | null>(null);

  // Form Details
  const todayStr = new Date().toISOString().split('T')[0];
  const [groomName, setGroomName] = useState<string>('Rahul');
  const [brideName, setBrideName] = useState<string>('Priya');
  const [personName, setPersonName] = useState<string>('Shreenika');
  const [birthdayAge, setBirthdayAge] = useState<string>('5th Birthday');
  const [subjectGender, setSubjectGender] = useState<'male' | 'female' | 'neutral'>('neutral');
  const [birthYear, setBirthYear] = useState<string>('');
  const [themeNotes, setThemeNotes] = useState<string>('');
  const [culturalHint, setCulturalHint] = useState<string>('');
  const [colorPalette, setColorPalette] = useState<string>('');
  const [eventTitle, setEventTitle] = useState<string>('');
  const [eventDate, setEventDate] = useState<string>(() => todayStr);
  const [eventTime, setEventTime] = useState<string>('5:00 PM Onwards');
  const [eventVenue, setEventVenue] = useState<string>('Malhar Resort, Pune');
  const [facePhotoUrl, setFacePhotoUrl] = useState<string>('');
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);

  // Aspect Ratio picker — initialised from selectedStyle.defaultAspectRatio when style is chosen
  const [selectedRatio, setSelectedRatio] = useState<CanonicalAspectRatio>('square');
  const supportsReferenceConditioning = selectedStyle?.supportsReferenceConditioning === true;

  // A user can select a reference-capable Flux style, attach a photo, then
  // choose a typography-first provider. Do not keep that stale local reference
  // around for a provider that cannot use it.
  useEffect(() => {
    if (!supportsReferenceConditioning && facePhotoUrl) {
      setFacePhotoUrl('');
      setPhotoPreview(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [supportsReferenceConditioning, facePhotoUrl]);

  const RATIO_META: Record<CanonicalAspectRatio, { icon: string; label: string; dims: string; desc: string }> = {
    square:    { icon: '⬛', label: 'Square',    dims: '1:1',  desc: 'Social post' },
    portrait:  { icon: '📱', label: 'Portrait',  dims: '4:5',  desc: 'Instagram' },
    landscape: { icon: '🖼️', label: 'Landscape', dims: '16:9', desc: 'Banner' },
    story:     { icon: '📲', label: 'Story',     dims: '9:16', desc: 'WhatsApp/Reels' },
  };

  // Generation State
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [generationResult, setGenerationResult] = useState<AiGenerationRecord | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [copiedLink, setCopiedLink] = useState<boolean>(false);

  // Fallback SVG generator for broken thumbnail URLs
  const getSvgFallback = (title: string, occ: string) => {
    const bg1 = occ === 'wedding' ? '#4c1d95' : '#065f46';
    const bg2 = occ === 'wedding' ? '#831843' : '#0891b2';
    const bg3 = occ === 'wedding' ? '#701a75' : '#4f46e5';
    const emoji = occ === 'wedding' ? '💍' : '✨';
    const cleanTitle = title.replace(/<|>|&/g, '');
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="250" viewBox="0 0 400 250">
      <rect width="400" height="250" fill="url(#g)"/>
      <defs>
        <linearGradient id="g" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="${bg1}" />
          <stop offset="50%" stop-color="${bg2}" />
          <stop offset="100%" stop-color="${bg3}" />
        </linearGradient>
      </defs>
      <circle cx="200" cy="100" r="45" fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.3)" stroke-width="2"/>
      <text x="200" y="108" font-family="sans-serif" font-size="28" fill="#ffffff" text-anchor="middle">${emoji}</text>
      <text x="200" y="175" font-family="sans-serif" font-size="15" fill="#ffffff" text-anchor="middle" font-weight="bold">${cleanTitle}</text>
      <text x="200" y="198" font-family="sans-serif" font-size="11" fill="rgba(255,255,255,0.7)" text-anchor="middle">AI ARTWORK STYLE</text>
    </svg>`;
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  };

  // Load active styles when occasion changes
  useEffect(() => {
    let isMounted = true;
    setLoadingStyles(true);
    setErrorMsg(null);

    fetchActiveAiStyles(occasion)
      .then((data) => {
        if (isMounted) {
          setStyles(data || []);
          const firstStyle = data?.[0] || null;
          setSelectedStyle(firstStyle);
          if (firstStyle) {
            setSelectedRatio(firstStyle.defaultAspectRatio ?? 'square');
          }
          if (!data || data.length === 0) {
            setErrorMsg('No AI styles are available for this occasion yet. Please check back soon.');
          }
        }
      })
      .catch((err) => {
        console.error('Failed to load AI styles:', err);
        if (isMounted) {
          setStyles([]);
          setSelectedStyle(null);
          setErrorMsg('Could not load AI styles right now. Please try again in a moment.');
        }
      })
      .finally(() => {
        if (isMounted) setLoadingStyles(false);
      });

    return () => {
      isMounted = false;
    };
  }, [occasion]);

  const loadMyGenerations = async () => {
    setLoadingMyGenerations(true);
    try {
      const data = await fetchMyAiGenerations();
      setMyGenerations(data || []);
    } catch (err) {
      console.error('Failed to load my AI generations:', err);
    } finally {
      setLoadingMyGenerations(false);
    }
  };

  useEffect(() => {
    loadMyGenerations();
  }, []);

  const handleOccasionSelect = (occ: 'wedding' | 'birthday') => {
    setOccasion(occ);
    if (occ === 'wedding') {
      setEventTitle('ROYAL WEDDING CELEBRATION');
    } else {
      setEventTitle('5TH BIRTHDAY CELEBRATION');
    }
    setStep(2);
  };

  // Local File Upload Handler
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setErrorMsg('Image file size must be under 10MB.');
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const dataUri = reader.result as string;
        setFacePhotoUrl(dataUri);
        setPhotoPreview(dataUri);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    setFacePhotoUrl('');
    setPhotoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleGenerate = async () => {
    if (!selectedStyle) {
      setErrorMsg('Please select a visual AI style template first.');
      return;
    }

    setIsGenerating(true);
    setErrorMsg(null);
    setGenerationResult(null);

    const inputPhotoRefs = supportsReferenceConditioning && facePhotoUrl.trim()
      ? [facePhotoUrl.trim()]
      : [];

    const slots: Record<string, string> = {
      eventTitle: eventTitle || (occasion === 'wedding' ? 'WEDDING CELEBRATION' : "YOU'RE INVITED!"),
      date: eventDate,
      time: eventTime,
      venue: eventVenue,
    };

    if (occasion === 'wedding') {
      slots.groomName = groomName || 'Groom';
      slots.brideName = brideName || 'Bride';
    } else {
      slots.personName = personName || 'Birthday Star';
      slots.age = birthdayAge;
      if (themeNotes.trim()) slots.themeNotes = themeNotes.trim();
      if (culturalHint.trim()) slots.culturalHint = culturalHint.trim();
      if (colorPalette.trim()) slots.colorPalette = colorPalette.trim();
    }

    try {
      const res = await generateAiArtifact({
        occasion,
        styleId: selectedStyle.id,
        inputPhotoRefs,
        slots,
        dryRun: false, // Hardcoded false for production user UI
        aspectRatio: selectedRatio,
        gender: subjectGender,
        birthYear: birthYear ? parseInt(birthYear, 10) : undefined,
      });

      setGenerationResult(res);
      loadMyGenerations();
      setStep(4);
    } catch (err: any) {
      console.error('AI Generation error:', err);
      const backendError =
        err?.data?.message || err?.message || 'Failed to generate AI image invitation';
      setErrorMsg(backendError);
    } finally {
      setIsGenerating(false);
    }
  };

  const copyImageLink = () => {
    if (generationResult?.resultImageUrl) {
      navigator.clipboard.writeText(generationResult.resultImageUrl);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2500);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 py-10 px-4 sm:px-6 lg:px-8">
      {/* Background Glow */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute -top-40 -left-40 w-96 h-96 bg-emerald-500/15 rounded-full blur-3xl" />
        <div className="absolute top-1/3 -right-40 w-96 h-96 bg-cyan-500/15 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 left-1/3 w-96 h-96 bg-purple-500/15 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto space-y-8">
        {/* Header Title Banner */}
        <div className="text-center space-y-3">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm font-semibold tracking-wide">
            <Sparkles className="w-4 h-4 animate-pulse" />
            AI-Native Artifact Studio
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight bg-gradient-to-r from-emerald-400 via-teal-300 to-cyan-400 bg-clip-text text-transparent">
            AI Image Invitation Studio
          </h1>
          <p className="text-slate-400 text-base sm:text-lg max-w-2xl mx-auto">
            Generate personalized, high-fidelity digital invitation artwork with optional face photo conditioning.
          </p>
        </div>

        {/* View Mode Navigation Tabs */}
        <div className="flex justify-center">
          <div className="inline-flex p-1 bg-slate-900/90 border border-slate-800 rounded-2xl backdrop-blur-xl gap-1">
            <button
              onClick={() => setViewMode('wizard')}
              className={`px-5 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 transition-all ${
                viewMode === 'wizard'
                  ? 'bg-emerald-500 text-slate-950 shadow-lg shadow-emerald-500/20'
                  : 'text-slate-400 hover:text-slate-200 cursor-pointer'
              }`}
            >
              <Wand2 className="w-4 h-4" />
              <span>Create AI Invitation</span>
            </button>
            <button
              onClick={() => {
                setViewMode('gallery');
                loadMyGenerations();
              }}
              className={`px-5 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 transition-all ${
                viewMode === 'gallery'
                  ? 'bg-emerald-500 text-slate-950 shadow-lg shadow-emerald-500/20'
                  : 'text-slate-400 hover:text-slate-200 cursor-pointer'
              }`}
            >
              <FileImage className="w-4 h-4" />
              <span>My AI Image Library</span>
              {myGenerations.length > 0 && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-mono font-extrabold ${
                  viewMode === 'gallery' ? 'bg-slate-950 text-emerald-400' : 'bg-slate-800 text-slate-300'
                }`}>
                  {myGenerations.length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Global Error Banner */}
        {errorMsg && (
          <div className="flex items-center gap-3 p-4 bg-rose-500/10 border border-rose-500/30 rounded-2xl text-rose-300 text-sm">
            <ShieldAlert className="w-5 h-5 flex-shrink-0 text-rose-400" />
            <span className="flex-1">{errorMsg}</span>
            <button
              onClick={() => setErrorMsg(null)}
              className="text-slate-400 hover:text-slate-200 text-xs underline"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* GALLERY VIEW MODE */}
        {viewMode === 'gallery' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-slate-900/90 backdrop-blur-xl border border-slate-800 p-6 rounded-3xl">
              <div>
                <h2 className="text-2xl font-bold text-slate-100 flex items-center gap-2">
                  <FileImage className="w-6 h-6 text-emerald-400" />
                  My Generated AI Images
                </h2>
                <p className="text-slate-400 text-sm mt-0.5">
                  Browse, download, and retrieve your previously generated invitation artwork.
                </p>
              </div>

              {/* Filter Pills & Refresh */}
              <div className="flex items-center gap-2 flex-wrap">
                {(['all', 'wedding', 'birthday'] as const).map((filter) => {
                  const count = filter === 'all'
                    ? myGenerations.length
                    : myGenerations.filter(g => g.occasion === filter).length;
                  return (
                    <button
                      key={filter}
                      onClick={() => setGalleryFilter(filter)}
                      className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold capitalize transition-all ${
                        galleryFilter === filter
                          ? 'bg-emerald-500/20 border border-emerald-500/50 text-emerald-300'
                          : 'bg-slate-950 border border-slate-800 text-slate-400 hover:text-slate-200'
                      }`}
                    >
                      {filter} ({count})
                    </button>
                  );
                })}

                <button
                  onClick={loadMyGenerations}
                  disabled={loadingMyGenerations}
                  className="p-2 rounded-xl bg-slate-950 border border-slate-800 text-slate-400 hover:text-emerald-400 transition-all disabled:opacity-50"
                  title="Refresh Library"
                >
                  <RefreshCw className={`w-4 h-4 ${loadingMyGenerations ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>

            {loadingMyGenerations ? (
              <div className="flex items-center justify-center py-20 text-slate-400 gap-3">
                <RefreshCw className="w-6 h-6 animate-spin text-emerald-400" />
                <span>Retrieving your AI invitation gallery...</span>
              </div>
            ) : (() => {
              const filteredList = myGenerations.filter((g) => {
                if (galleryFilter === 'all') return true;
                return g.occasion === galleryFilter;
              });

              if (filteredList.length === 0) {
                return (
                  <div className="flex flex-col items-center justify-center py-16 px-4 bg-slate-900/60 border border-slate-800 rounded-3xl text-center space-y-4">
                    <div className="w-16 h-16 rounded-2xl bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-500">
                      <ImageIcon className="w-8 h-8" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-200">
                        {galleryFilter === 'all'
                          ? 'No AI Invitations Generated Yet'
                          : `No ${galleryFilter} Invitations Found`}
                      </h3>
                      <p className="text-slate-400 text-xs mt-1 max-w-sm">
                        Create your first AI image invitation and it will automatically appear in your library for easy retrieval.
                      </p>
                    </div>
                    <button
                      onClick={() => setViewMode('wizard')}
                      className="px-6 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-xs flex items-center gap-2 transition-all shadow-lg shadow-emerald-500/20"
                    >
                      <Wand2 className="w-4 h-4" />
                      <span>Generate First AI Invitation</span>
                    </button>
                  </div>
                );
              }

              return (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredList.map((item) => {
                    const isSuccess = item.status === 'SUCCESS' || item.status === 'READY';
                    const isModerated = item.status === 'MODERATED_REJECTED';

                    return (
                      <div
                        key={item.id}
                        className="group relative rounded-3xl overflow-hidden bg-slate-900/90 border border-slate-800 hover:border-emerald-500/50 backdrop-blur-xl transition-all duration-300 flex flex-col hover:shadow-xl hover:shadow-emerald-500/10"
                      >
                        {/* Image Preview Container */}
                        <div className="relative h-56 w-full bg-slate-950 overflow-hidden flex items-center justify-center">
                          {isSuccess && item.resultImageUrl ? (
                            <img
                              src={item.resultImageUrl}
                              alt={item.styleId || 'AI Invitation'}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          ) : (
                            <div className="p-6 text-center space-y-2">
                              <ShieldAlert className="w-8 h-8 mx-auto text-rose-400" />
                              <p className="text-xs text-rose-300 font-semibold">
                                {isModerated ? 'Safety Moderation Filtered' : 'Generation Error'}
                              </p>
                              <p className="text-[10px] text-slate-500">0 credits charged</p>
                            </div>
                          )}

                          {/* Top Badges */}
                          <div className="absolute top-3 left-3 flex items-center gap-2 z-10">
                            <span className="px-2.5 py-1 rounded-full bg-slate-950/80 backdrop-blur-md border border-slate-700 text-emerald-400 text-[11px] font-bold capitalize">
                              {item.occasion}
                            </span>
                            {item.aspectRatio && (
                              <span className="px-2 py-0.5 rounded-full bg-slate-950/80 backdrop-blur-md border border-slate-800 text-slate-300 text-[10px] font-mono">
                                {item.aspectRatio}
                              </span>
                            )}
                          </div>

                          {/* Status Badge */}
                          <div className="absolute top-3 right-3 z-10">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                              isSuccess
                                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                                : 'bg-rose-500/20 text-rose-400 border border-rose-500/30'
                            }`}>
                              {item.status}
                            </span>
                          </div>

                          {/* Hover Action Overlay (Success items) */}
                          {isSuccess && item.resultImageUrl && (
                            <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3 p-4">
                              <button
                                onClick={() => setSelectedGalleryItem(item)}
                                className="p-3 rounded-2xl bg-emerald-500 text-slate-950 font-bold hover:scale-110 transition-transform shadow-lg shadow-emerald-500/30"
                                title="View Fullscreen"
                              >
                                <Eye className="w-5 h-5" />
                              </button>
                              <a
                                href={item.resultImageUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                download={`ai-invitation-${item.id}.jpg`}
                                className="p-3 rounded-2xl bg-slate-800 text-slate-100 font-bold hover:bg-slate-700 hover:scale-110 transition-all border border-slate-700"
                                title="Download Image"
                              >
                                <Download className="w-5 h-5" />
                              </a>
                              <button
                                onClick={() => {
                                  if (item.resultImageUrl) {
                                    navigator.clipboard.writeText(item.resultImageUrl);
                                    setCopiedLink(true);
                                    setTimeout(() => setCopiedLink(false), 2500);
                                  }
                                }}
                                className="p-3 rounded-2xl bg-slate-800 text-slate-100 font-bold hover:bg-slate-700 hover:scale-110 transition-all border border-slate-700"
                                title="Copy Link"
                              >
                                <Copy className="w-5 h-5" />
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Card Details Footer */}
                        <div className="p-4 space-y-2 border-t border-slate-800/80">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-bold text-slate-200 capitalize">
                              {item.styleId ? item.styleId.replace(/-/g, ' ') : 'AI Artwork'}
                            </span>
                            <span className="text-[10px] text-slate-500">
                              {new Date(item.createdAt).toLocaleDateString(undefined, {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })}
                            </span>
                          </div>

                          {item.slots && (
                            <p className="text-[11px] text-slate-400 line-clamp-1">
                              {item.slots.eventTitle || item.slots.groomName || item.slots.personName || 'Custom Celebration'}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        )}

        {/* WIZARD VIEW MODE */}
        {viewMode === 'wizard' && (
          <>
            {/* Stepper Navigation */}
            <div className="flex items-center justify-between max-w-2xl mx-auto px-4 py-3 bg-slate-900/80 backdrop-blur-xl border border-slate-800 rounded-2xl">
              {[
                { num: 1, label: 'Occasion' },
                { num: 2, label: 'Style Gallery' },
                { num: 3, label: 'Customize' },
                { num: 4, label: 'Result' },
              ].map((s) => (
                <button
                  key={s.num}
                  onClick={() => {
                    if (s.num < step) setStep(s.num);
                  }}
                  disabled={s.num > step}
                  className={`flex items-center gap-2 text-sm font-semibold transition-all ${
                    step === s.num
                      ? 'text-emerald-400 font-bold'
                      : step > s.num
                      ? 'text-slate-300 hover:text-emerald-300 cursor-pointer'
                      : 'text-slate-600 cursor-not-allowed'
                  }`}
                >
                  <div
                    className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                      step === s.num
                        ? 'bg-emerald-500 text-slate-950 shadow-lg shadow-emerald-500/30'
                        : step > s.num
                        ? 'bg-slate-700 text-emerald-400'
                        : 'bg-slate-800 text-slate-500'
                    }`}
                  >
                    {step > s.num ? <CheckCircle2 className="w-4 h-4" /> : s.num}
                  </div>
                  <span className="hidden sm:inline">{s.label}</span>
                </button>
              ))}
            </div>

            {/* STEP 1: Occasion Pick */}
            {step === 1 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                {/* Wedding Card */}
                <div
                  onClick={() => handleOccasionSelect('wedding')}
                  className="group cursor-pointer p-8 rounded-3xl bg-slate-900/80 border border-slate-800 hover:border-emerald-500/50 backdrop-blur-xl transition-all duration-300 hover:shadow-2xl hover:shadow-emerald-500/10 flex flex-col items-center text-center space-y-4"
                >
                  <div className="w-16 h-16 rounded-2xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400 group-hover:scale-110 transition-transform">
                    <Heart className="w-8 h-8 fill-amber-400/20" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-100 group-hover:text-amber-300">
                    Wedding & Marriage
                  </h2>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    Royal heritage, floral garden, and traditional Indian wedding invitation themes with couple photo identity preservation.
                  </p>

                  <div className="inline-flex items-center gap-1.5 text-amber-400 text-xs font-semibold pt-2">
                    <span>Select Wedding Styles</span>
                    <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>

                {/* Birthday Card */}
                <div
                  onClick={() => handleOccasionSelect('birthday')}
                  className="group cursor-pointer p-8 rounded-3xl bg-slate-900/80 border border-slate-800 hover:border-cyan-500/50 backdrop-blur-xl transition-all duration-300 hover:shadow-2xl hover:shadow-cyan-500/10 flex flex-col items-center text-center space-y-4"
                >
                  <div className="w-16 h-16 rounded-2xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center text-cyan-400 group-hover:scale-110 transition-transform">
                    <PartyPopper className="w-8 h-8" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-100 group-hover:text-cyan-300">
                    Birthday Bash
                  </h2>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    Disney 3D Princess, Unicorn Rainbow, Mermaid Kingdom, Teddy Party, and Cyberpunk invitation cards with face embedding.
                  </p>

                  <div className="inline-flex items-center gap-1.5 text-cyan-400 text-xs font-semibold pt-2">
                    <span>Select Birthday Styles</span>
                    <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
            )}

            {/* STEP 2: Style Gallery */}
            {step === 2 && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-slate-100 capitalize">
                      {occasion} AI Style Gallery
                    </h2>
                    <p className="text-slate-400 text-sm">
                      Select a curated visual direction for your AI invitation card.
                    </p>
                  </div>
                  <button
                    onClick={() => setStep(1)}
                    className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-200"
                  >
                    <ChevronLeft className="w-4 h-4" /> Change Occasion
                  </button>
                </div>

                {loadingStyles ? (
                  <div className="flex items-center justify-center py-16 text-slate-400 gap-3">
                    <RefreshCw className="w-6 h-6 animate-spin text-emerald-400" />
                    <span>Loading active AI styles...</span>
                  </div>
                ) : styles.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center gap-3 text-slate-400">
                    <AlertCircle className="w-8 h-8 text-amber-400" />
                    <p>{errorMsg || 'No AI styles are available for this occasion yet.'}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {styles.map((st) => {
                      const isSelected = selectedStyle?.id === st.id;
                      return (
                        <div
                          key={st.id}
                          onClick={() => {
                            setSelectedStyle(st);
                            setSelectedRatio(st.defaultAspectRatio ?? 'square');
                          }}
                          className={`group cursor-pointer rounded-3xl overflow-hidden bg-slate-900/90 border backdrop-blur-xl transition-all duration-300 flex flex-col ${
                            isSelected
                              ? 'border-emerald-400 shadow-xl shadow-emerald-500/20 ring-2 ring-emerald-400/30 scale-[1.02]'
                              : 'border-slate-800 hover:border-slate-700 hover:scale-[1.01]'
                          }`}
                        >
                          {/* Image Preview Thumbnail with Error Fallback */}
                          <div className="relative h-48 w-full overflow-hidden bg-slate-950">
                            <img
                              src={st.thumbnailUrl || getSvgFallback(st.name, st.occasion)}
                              alt={st.name}
                              onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = getSvgFallback(st.name, st.occasion);
                              }}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent opacity-80" />

                            {/* Top Badges */}
                            <div className="absolute top-3 left-3 flex items-center gap-2">
                              <span className="px-2.5 py-1 rounded-full bg-slate-950/80 backdrop-blur-md border border-slate-700 text-emerald-400 text-xs font-semibold flex items-center gap-1">
                                <Sparkles className="w-3 h-3" />
                                {st.supportsReferenceConditioning
                                  ? st.maxFaces > 1 ? `Up to ${st.maxFaces} Faces` : '1 Face Embedding'
                                  : 'Typography-first'}
                              </span>
                            </div>

                            {isSelected && (
                              <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-emerald-500 text-slate-950 flex items-center justify-center shadow-lg">
                                <CheckCircle2 className="w-5 h-5" />
                              </div>
                            )}

                            <div className="absolute bottom-3 left-3 right-3">
                              <h3 className="text-lg font-bold text-slate-100 line-clamp-1">
                                {st.name}
                              </h3>
                            </div>
                          </div>

                          {/* Card Content */}
                          <div className="p-4 flex-1 flex flex-col justify-between space-y-3">
                            <p className="text-xs text-slate-400 line-clamp-3 leading-relaxed">
                              {st.promptTemplate}
                            </p>

                            <div className="pt-2 border-t border-slate-800 flex items-center justify-between text-xs text-slate-400">
                              <span>Text Mode: <strong className="text-slate-300 uppercase">{st.defaultTextRenderMode || 'overlay'}</strong></span>
                              <span className="text-emerald-400 font-semibold">1st Gen FREE</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="flex justify-end pt-4">
                  <button
                    onClick={() => setStep(3)}
                    disabled={!selectedStyle}
                    className="px-6 py-3 rounded-2xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold flex items-center gap-2 transition-all shadow-lg shadow-emerald-500/25 disabled:opacity-50 cursor-pointer"
                  >
                    <span>Continue to Customization</span>
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

            {/* STEP 3: Customize Details & Face Reference */}
            {step === 3 && selectedStyle && (
              <div className="bg-slate-900/90 backdrop-blur-xl border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-8">
                {/* FREE GENERATION PROMINENT BADGE */}
                <div className="flex items-center gap-3.5 p-4 bg-gradient-to-r from-emerald-500/20 via-teal-500/15 to-cyan-500/20 border border-emerald-500/40 rounded-2xl">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center text-emerald-400 flex-shrink-0">
                    <Crown className="w-6 h-6 animate-pulse" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-extrabold text-emerald-300 flex items-center gap-2">
                      🎁 100% FREE — Your First AI Invitation Generation is on us!
                    </h4>
                    <p className="text-xs text-slate-300">
                      Every user gets 1 free AI invitation card generation grant. Zero credits debited.
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between border-b border-slate-800 pb-4">
                  <div>
                    <h2 className="text-2xl font-bold text-slate-100">
                      Event Details & Face Conditioning
                    </h2>
                    <p className="text-slate-400 text-sm">
                      Selected Style: <span className="text-emerald-400 font-semibold">{selectedStyle.name}</span>
                    </p>
                  </div>
                  <button
                    onClick={() => setStep(2)}
                    className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-200 cursor-pointer"
                  >
                    <ChevronLeft className="w-4 h-4" /> Change Style
                  </button>
                </div>

                {/* Aspect Ratio Picker */}
                {(() => {
                  const allowedRatios: CanonicalAspectRatio[] =
                    selectedStyle.allowedAspectRatios?.length
                      ? selectedStyle.allowedAspectRatios
                      : ['square'];
                  return (
                    <div className="space-y-3">
                      <div>
                        <h3 className="text-sm font-bold text-slate-200 mb-0.5">Output Format</h3>
                        <p className="text-xs text-slate-400">Select the aspect ratio for your invitation.</p>
                      </div>
                      <div className="flex flex-wrap gap-3">
                        {allowedRatios.map((ratio) => {
                          const meta = RATIO_META[ratio];
                          const isSelected = selectedRatio === ratio;
                          const isDefault = ratio === (selectedStyle.defaultAspectRatio ?? 'square');
                          return (
                            <button
                              key={ratio}
                              type="button"
                              onClick={() => setSelectedRatio(ratio)}
                              className={`relative flex flex-col items-center gap-1 px-4 py-3 rounded-2xl border transition-all duration-200 min-w-[80px] cursor-pointer ${
                                isSelected
                                  ? 'border-emerald-400 bg-emerald-500/10 ring-2 ring-emerald-400/30 shadow-lg shadow-emerald-500/10 scale-105'
                                  : 'border-slate-700 bg-slate-900/60 hover:border-slate-600 hover:bg-slate-800/60'
                              }`}
                            >
                              <div
                                className={`rounded-sm border-2 ${
                                  isSelected ? 'border-emerald-400 bg-emerald-500/20' : 'border-slate-600 bg-slate-800'
                                }`}
                                style={{
                                  width:  ratio === 'landscape' ? 36 : ratio === 'portrait' ? 22 : ratio === 'story' ? 18 : 28,
                                  height: ratio === 'landscape' ? 22 : ratio === 'portrait' ? 28 : ratio === 'story' ? 36 : 28,
                                }}
                              />
                              <span className={`text-xs font-semibold capitalize ${
                                isSelected ? 'text-emerald-400' : 'text-slate-300'
                              }`}>
                                {meta.label}
                              </span>
                              <span className={`text-[10px] font-mono ${
                                isSelected ? 'text-emerald-500/80' : 'text-slate-500'
                              }`}>
                                {meta.dims}
                              </span>
                              {isDefault && !isSelected && (
                                <span className="absolute -top-2 -right-2 text-[9px] bg-slate-700 text-slate-400 rounded-full px-1.5 py-0.5 font-medium">★ default</span>
                              )}
                              {isSelected && (
                                <span className="absolute -top-2 -right-2 text-[9px] bg-emerald-500 text-slate-950 rounded-full px-1.5 py-0.5 font-bold">✓</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                      <p className="text-[11px] text-slate-500">
                        {RATIO_META[selectedRatio]?.desc} · {RATIO_META[selectedRatio]?.dims}
                      </p>
                    </div>
                  );
                })()}

                {/* Input Form Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {occasion === 'wedding' ? (
                    <>
                      <div>
                        <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                          Groom Name
                        </label>
                        <div className="relative">
                          <User className="w-4 h-4 absolute left-3 top-3.5 text-slate-500" />
                          <input
                            type="text"
                            value={groomName}
                            onChange={(e) => setGroomName(e.target.value)}
                            placeholder="e.g. Rahul"
                            className="w-full pl-10 pr-4 py-3 bg-slate-950 border border-slate-800 rounded-xl text-slate-100 text-sm focus:outline-none focus:border-emerald-500"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                          Bride Name
                        </label>
                        <div className="relative">
                          <User className="w-4 h-4 absolute left-3 top-3.5 text-slate-500" />
                          <input
                            type="text"
                            value={brideName}
                            onChange={(e) => setBrideName(e.target.value)}
                            placeholder="e.g. Priya"
                            className="w-full pl-10 pr-4 py-3 bg-slate-950 border border-slate-800 rounded-xl text-slate-100 text-sm focus:outline-none focus:border-emerald-500"
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                          Birthday Star Name
                        </label>
                        <div className="relative">
                          <User className="w-4 h-4 absolute left-3 top-3.5 text-slate-500" />
                          <input
                            type="text"
                            value={personName}
                            onChange={(e) => setPersonName(e.target.value)}
                            placeholder="e.g. Shreenika"
                            className="w-full pl-10 pr-4 py-3 bg-slate-950 border border-slate-800 rounded-xl text-slate-100 text-sm focus:outline-none focus:border-emerald-500"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                          Age / Milestone Hint
                        </label>
                        <div className="relative">
                          <Gift className="w-4 h-4 absolute left-3 top-3.5 text-slate-500" />
                          <input
                            type="text"
                            value={birthdayAge}
                            onChange={(e) => setBirthdayAge(e.target.value)}
                            placeholder="e.g. 5th Birthday"
                            className="w-full pl-10 pr-4 py-3 bg-slate-950 border border-slate-800 rounded-xl text-slate-100 text-sm focus:outline-none focus:border-emerald-500"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                          Subject Gender Context
                        </label>
                        <div className="flex items-center gap-1.5 p-1 bg-slate-950 border border-slate-800 rounded-xl">
                          <button
                            type="button"
                            onClick={() => setSubjectGender('male')}
                            className={`flex-1 py-2 text-xs font-medium rounded-lg transition-colors cursor-pointer ${
                              subjectGender === 'male'
                                ? 'bg-emerald-500 text-slate-950 font-bold'
                                : 'text-slate-400 hover:text-slate-200'
                            }`}
                          >
                            👦 Male
                          </button>
                          <button
                            type="button"
                            onClick={() => setSubjectGender('female')}
                            className={`flex-1 py-2 text-xs font-medium rounded-lg transition-colors cursor-pointer ${
                              subjectGender === 'female'
                                ? 'bg-emerald-500 text-slate-950 font-bold'
                                : 'text-slate-400 hover:text-slate-200'
                            }`}
                          >
                            👧 Female
                          </button>
                          <button
                            type="button"
                            onClick={() => setSubjectGender('neutral')}
                            className={`flex-1 py-2 text-xs font-medium rounded-lg transition-colors cursor-pointer ${
                              subjectGender === 'neutral'
                                ? 'bg-slate-800 text-slate-200 font-bold'
                                : 'text-slate-400 hover:text-slate-200'
                            }`}
                          >
                            ✨ Auto
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                          Birth Year (Optional)
                        </label>
                        <div className="relative">
                          <Calendar className="w-4 h-4 absolute left-3 top-3.5 text-slate-500" />
                          <input
                            type="number"
                            min="1900"
                            max={new Date().getFullYear()}
                            value={birthYear}
                            onChange={(e) => setBirthYear(e.target.value)}
                            placeholder="e.g. 2021 or 1966"
                            className="w-full pl-10 pr-4 py-3 bg-slate-950 border border-slate-800 rounded-xl text-slate-100 text-sm focus:outline-none focus:border-emerald-500 font-mono"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                      Event Title / Headline
                    </label>
                    <input
                      type="text"
                      value={eventTitle}
                      onChange={(e) => setEventTitle(e.target.value)}
                      placeholder={occasion === 'wedding' ? 'WEDDING CELEBRATION' : "5TH BIRTHDAY CELEBRATION"}
                      className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-xl text-slate-100 text-sm focus:outline-none focus:border-emerald-500"
                    />
                  </div>

                  {/* Event Date */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                      Event Date
                    </label>
                    <div className="relative">
                      <Calendar className="w-4 h-4 absolute left-3 top-3.5 text-slate-500 pointer-events-none" />
                      <input
                        type="date"
                        min={todayStr}
                        value={eventDate}
                        onChange={(e) => setEventDate(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 bg-slate-950 border border-slate-800 rounded-xl text-slate-100 text-sm focus:outline-none focus:border-emerald-500 [color-scheme:dark]"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                      Event Time
                    </label>
                    <div className="relative">
                      <Clock className="w-4 h-4 absolute left-3 top-3.5 text-slate-500" />
                      <input
                        type="text"
                        value={eventTime}
                        onChange={(e) => setEventTime(e.target.value)}
                        placeholder="e.g. 5:00 PM Onwards"
                        className="w-full pl-10 pr-4 py-3 bg-slate-950 border border-slate-800 rounded-xl text-slate-100 text-sm focus:outline-none focus:border-emerald-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                      Venue & City
                    </label>
                    <div className="relative">
                      <MapPin className="w-4 h-4 absolute left-3 top-3.5 text-slate-500" />
                      <input
                        type="text"
                        value={eventVenue}
                        onChange={(e) => setEventVenue(e.target.value)}
                        placeholder="e.g. Malhar Resort, Pune"
                        className="w-full pl-10 pr-4 py-3 bg-slate-950 border border-slate-800 rounded-xl text-slate-100 text-sm focus:outline-none focus:border-emerald-500"
                      />
                    </div>
                  </div>

                  {occasion === 'birthday' && (
                    <>
                      <div>
                        <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                          Theme Notes <span className="text-slate-500 font-normal">(optional)</span>
                        </label>
                        <input
                          type="text"
                          value={themeNotes}
                          onChange={(e) => setThemeNotes(e.target.value)}
                          placeholder="e.g. space explorer, botanical garden, modern minimal"
                          className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-xl text-slate-100 text-sm focus:outline-none focus:border-emerald-500"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                          Cultural Inspiration <span className="text-slate-500 font-normal">(optional)</span>
                        </label>
                        <input
                          type="text"
                          value={culturalHint}
                          onChange={(e) => setCulturalHint(e.target.value)}
                          placeholder="e.g. Japanese washi, Indian floral geometry, Nordic"
                          className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-xl text-slate-100 text-sm focus:outline-none focus:border-emerald-500"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                          Colour Palette <span className="text-slate-500 font-normal">(optional)</span>
                        </label>
                        <input
                          type="text"
                          value={colorPalette}
                          onChange={(e) => setColorPalette(e.target.value)}
                          placeholder="e.g. sage, cream and soft gold"
                          className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-xl text-slate-100 text-sm focus:outline-none focus:border-emerald-500"
                        />
                      </div>
                    </>
                  )}
                </div>

                {/* LOCAL DEVICE PHOTO UPLOAD & REFERENCE */}
                {supportsReferenceConditioning ? (
                  <div className="pt-4 border-t border-slate-800 space-y-4">
                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-bold text-slate-200 flex items-center gap-2">
                        <ImageIcon className="w-4 h-4 text-emerald-400" />
                        Upload Face Photo from Device (Optional — Identity Preservation)
                      </label>
                      <span className="text-xs text-slate-400">
                        {selectedStyle.maxFaces > 1 ? `Up to ${selectedStyle.maxFaces} faces` : 'Single Face'}
                      </span>
                    </div>
                    <p className="text-xs text-slate-400 leading-relaxed">
                      Select a clear photo from your computer or mobile device. This selected provider supports reference conditioning, so the generation uses it to guide the birthday person's identity; every upload is moderated first.
                    </p>

                    {/* Photo Upload Zone */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      className="hidden"
                    />

                    {photoPreview ? (
                      <div className="flex items-center gap-4 p-4 bg-slate-950 border border-slate-800 rounded-2xl">
                        <div className="relative w-20 h-20 rounded-xl overflow-hidden border border-emerald-500/50 bg-slate-900">
                          <img
                            src={photoPreview}
                            alt="Uploaded face reference"
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 space-y-1">
                          <span className="text-xs font-bold text-emerald-400 flex items-center gap-1">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Photo Attached Successfully
                          </span>
                          <p className="text-[11px] text-slate-400">
                            Face identity preservation ready for generation.
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={handleRemovePhoto}
                          className="p-2 rounded-xl bg-rose-500/10 text-rose-400 hover:bg-rose-500/20 transition-all flex items-center gap-1 text-xs font-semibold cursor-pointer"
                        >
                          <X className="w-4 h-4" /> Remove
                        </button>
                      </div>
                    ) : (
                      <div
                        onClick={() => fileInputRef.current?.click()}
                        className="group cursor-pointer border-2 border-dashed border-slate-800 hover:border-emerald-500/50 rounded-2xl p-6 text-center bg-slate-950/50 hover:bg-slate-900/50 transition-all space-y-2 flex flex-col items-center justify-center"
                      >
                        <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400 group-hover:scale-110 transition-transform">
                          <UploadCloud className="w-6 h-6" />
                        </div>
                        <div>
                          <span className="text-xs font-bold text-slate-200 group-hover:text-emerald-400">
                            Click to choose photo file from device
                          </span>
                          <p className="text-[11px] text-slate-500 mt-0.5">
                            Supports JPG, PNG, WEBP up to 10MB
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="pt-4 border-t border-slate-800 rounded-xl bg-slate-900/40 p-4 text-xs text-slate-400">
                    This style is optimized for AI-rendered typography and does not yet support face-reference conditioning. Choose a Flux reference-capable style to include the birthday person's photo.
                  </div>
                )}

                {/* Action Trigger Button */}
                <div className="flex justify-between items-center pt-4 border-t border-slate-800">
                  <div className="text-xs text-slate-400 flex items-center gap-1.5">
                    <Crown className="w-4 h-4 text-amber-400" />
                    <span>1st Generation FREE · No Credit Card Needed</span>
                  </div>

                  <button
                    onClick={handleGenerate}
                    disabled={isGenerating}
                    className="px-8 py-3.5 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-400 hover:from-emerald-400 hover:to-teal-300 text-slate-950 font-extrabold flex items-center gap-2.5 transition-all shadow-xl shadow-emerald-500/25 disabled:opacity-50 cursor-pointer"
                  >
                    {isGenerating ? (
                      <>
                        <RefreshCw className="w-5 h-5 animate-spin" />
                        <span>Generating Artwork...</span>
                      </>
                    ) : (
                      <>
                        <Wand2 className="w-5 h-5" />
                        <span>Generate AI Invitation Card</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* STEP 4: Result Display & Share Actions */}
            {step === 4 && generationResult && (
              <div className="bg-slate-900/90 backdrop-blur-xl border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-6">
                <div className="flex items-center justify-between border-b border-slate-800 pb-4">
                  <div>
                    <h2 className="text-2xl font-bold text-slate-100 flex items-center gap-2">
                      <Sparkles className="w-6 h-6 text-emerald-400" />
                      Your AI Invitation Artwork is Ready!
                    </h2>
                    <p className="text-slate-400 text-sm">
                      Generation ID: <code className="text-emerald-400">{generationResult.id}</code>
                    </p>
                  </div>
                  <span className="px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-bold flex items-center gap-1">
                    <Gift className="w-3.5 h-3.5" />
                    1st Gen Free Used
                  </span>
                </div>

                {/* Generated Image Showcase */}
                <div className="flex flex-col items-center space-y-4">
                  <div className="relative max-w-lg w-full rounded-2xl overflow-hidden border-2 border-slate-800 shadow-2xl bg-slate-950 group">
                    <img
                      src={generationResult.resultImageUrl || ''}
                      alt="Generated AI Invitation Card"
                      className="w-full h-auto object-cover"
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap items-center justify-center gap-4 pt-2">
                    <a
                      href={generationResult.resultImageUrl || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      download="ai-invitation-card.jpg"
                      className="px-6 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold flex items-center gap-2 transition-all shadow-lg shadow-emerald-500/20"
                    >
                      <Download className="w-4 h-4" />
                      Download Artwork
                    </a>

                    <button
                      onClick={copyImageLink}
                      className="px-6 py-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-100 font-semibold flex items-center gap-2 transition-all border border-slate-700 cursor-pointer"
                    >
                      {copiedLink ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                      <span>{copiedLink ? 'Link Copied!' : 'Copy Image Link'}</span>
                    </button>

                    <button
                      onClick={() => {
                        setViewMode('gallery');
                        loadMyGenerations();
                      }}
                      className="px-6 py-3 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 font-semibold flex items-center gap-2 transition-all border border-emerald-500/40 cursor-pointer"
                    >
                      <FileImage className="w-4 h-4 text-emerald-400" />
                      <span>View in My Library</span>
                    </button>

                    <button
                      onClick={() => setStep(2)}
                      className="px-6 py-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-100 font-semibold flex items-center gap-2 transition-all border border-slate-700 cursor-pointer"
                    >
                      <RefreshCw className="w-4 h-4 text-cyan-400" />
                      <span>Generate Another</span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* LIGHTBOX MODAL PREVIEW */}
        {selectedGalleryItem && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-fade-in">
            <div className="relative max-w-4xl w-full max-h-[90vh] bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden shadow-2xl flex flex-col md:flex-row">
              {/* Image Preview Side */}
              <div className="flex-1 bg-slate-950 flex items-center justify-center p-4 min-h-[300px]">
                {selectedGalleryItem.resultImageUrl ? (
                  <img
                    src={selectedGalleryItem.resultImageUrl}
                    alt={selectedGalleryItem.styleId || 'AI Invitation Artwork'}
                    className="max-h-[75vh] w-auto object-contain rounded-xl shadow-lg"
                  />
                ) : (
                  <div className="text-center p-6 space-y-2 text-rose-400">
                    <ShieldAlert className="w-12 h-12 mx-auto" />
                    <p className="font-bold">Image Unavailable</p>
                  </div>
                )}
              </div>

              {/* Sidebar Details */}
              <div className="w-full md:w-80 p-6 bg-slate-900 flex flex-col justify-between space-y-6 border-t md:border-t-0 md:border-l border-slate-800">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-bold capitalize">
                      {selectedGalleryItem.occasion}
                    </span>
                    <button
                      onClick={() => setSelectedGalleryItem(null)}
                      className="p-1.5 rounded-full bg-slate-800 text-slate-400 hover:text-slate-100 hover:bg-slate-700 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  <div>
                    <h3 className="text-lg font-bold text-slate-100 capitalize">
                      {selectedGalleryItem.styleId ? selectedGalleryItem.styleId.replace(/-/g, ' ') : 'AI Invitation'}
                    </h3>
                    <p className="text-xs text-slate-400 mt-1">
                      Created on {new Date(selectedGalleryItem.createdAt).toLocaleString()}
                    </p>
                  </div>

                  <div className="space-y-2 text-xs text-slate-300 bg-slate-950 p-3 rounded-xl border border-slate-800">
                    {selectedGalleryItem.aspectRatio && (
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Aspect Ratio:</span>
                        <span className="font-mono text-emerald-400">{selectedGalleryItem.aspectRatio}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">Status:</span>
                      <span className="font-bold text-emerald-400">{selectedGalleryItem.status}</span>
                    </div>
                    {selectedGalleryItem.id && (
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">ID:</span>
                        <span className="font-mono text-[10px] text-slate-400 truncate max-w-[140px]">
                          {selectedGalleryItem.id}
                        </span>
                      </div>
                    )}
                  </div>

                  {selectedGalleryItem.slots && (
                    <div className="space-y-1">
                      <span className="text-xs font-semibold text-slate-400">Event Details:</span>
                      <div className="text-xs text-slate-300 space-y-0.5 bg-slate-950/60 p-2.5 rounded-xl border border-slate-800/80">
                        {Object.entries(selectedGalleryItem.slots).map(([k, v]) => (
                          <div key={k} className="flex justify-between gap-2">
                            <span className="text-slate-500 capitalize">{k}:</span>
                            <span className="text-slate-200 font-medium truncate">{String(v)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                {selectedGalleryItem.resultImageUrl && (
                  <div className="space-y-2 pt-2 border-t border-slate-800">
                    <a
                      href={selectedGalleryItem.resultImageUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      download={`ai-invitation-${selectedGalleryItem.id}.jpg`}
                      className="w-full py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-xs flex items-center justify-center gap-2 transition-all shadow-md shadow-emerald-500/20"
                    >
                      <Download className="w-4 h-4" />
                      Download Image
                    </a>
                    <button
                      onClick={() => {
                        if (selectedGalleryItem.resultImageUrl) {
                          navigator.clipboard.writeText(selectedGalleryItem.resultImageUrl);
                          setCopiedLink(true);
                          setTimeout(() => setCopiedLink(false), 2500);
                        }
                      }}
                      className="w-full py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 font-semibold text-xs flex items-center justify-center gap-2 transition-all border border-slate-700 cursor-pointer"
                    >
                      {copiedLink ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                      <span>{copiedLink ? 'Link Copied!' : 'Copy Image Link'}</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AiInvitationsPage;
